<template>
  <div
    v-if="items"
    :class="color && `${color}-container`"
    class="text-on-surface-variant mt-2 mb-3 flex flex-row items-center justify-center gap-2.5 leading-0!"
  >
    <span class="text-body-medium inline-flex items-center gap-1">
      <BaseIcon size="18" :class="[showAsBar ? '-rotate-90' : '', color && 'text-primary']">{{
        showAsBar ? 'side_navigation' : 'network_wifi_2_bar'
      }}</BaseIcon>
      <span class="text-label-large">
        <template v-if="items.mainValue === 'others'">{{ $t('perspectives.others') }}</template>
        <template v-else>{{ labelForKey(items.mainValue) }}</template>
      </span>
    </span>
    <div
      class="overflow-hidden transition-all duration-300"
      :class="!active ? 'max-w-0' : 'max-w-full'"
    >
      <PerspectiveLegend :all-perspectives="topicPerspectives ?? perspectives" :perspectives="items.perspectives" />
    </div>
  </div>
</template>
<script lang="ts" setup>
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import { SELF_PERSPECTIVE_KEY } from '@/core/models/perspective'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import type { Perspective } from '@/core/models/perspective.ts'
  import PerspectiveLegend from './question-list/PerspectiveLegend.vue'
  interface Props {
    perspectives: Perspective[]
    topicPerspectives?: ReadonlyArray<Perspective>
    active: boolean
    showAsBar?: boolean
    color?: string
  }

  const { perspectives, topicPerspectives } = defineProps<Props>()
  const { t } = useI18n()

  const labelForKey = (key: string) => {
    if (key === SELF_PERSPECTIVE_KEY) {
      return t(`perspectives.${key}`)
    }

    return resolvePerspectiveLabel(key, perspectives)
  }

  const perspectivesSet = computed(() => {
    return new Set(perspectives.map((p) => p.key))
  })

  const items = computed(() => {
    const hasSelf = perspectivesSet.value.has(SELF_PERSPECTIVE_KEY)
    const others = perspectives.filter((p) => p.key !== SELF_PERSPECTIVE_KEY).map((p) => p.key)
    const hasOthers = others.length > 0

    if (hasSelf && hasOthers) {
      return {
        mainValue: 'others',
        perspectives: [SELF_PERSPECTIVE_KEY, ...others],
      }
    }
    if (hasSelf && !hasOthers) {
      return { mainValue: SELF_PERSPECTIVE_KEY, perspectives: [] as string[] }
    }
    if (!hasSelf && hasOthers && others.length == 1) {
      return { mainValue: others[0], perspectives: [] as string[] }
    }
    if (!hasSelf && hasOthers) {
      return { mainValue: 'others', perspectives: others as string[] }
    }

    return null
  })
</script>
